import { useState } from 'react';
import { Plus, Pencil, Trash2, FolderPlus } from 'lucide-react';
import { useProducts } from '../../contexts/ProductContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { DataTable, type Column } from '../ui/DataTable';
import type { Product, Category } from '@jigflow/shared';
import ProductEditor from '../product/ProductEditor';

export default function CatalogTab() {
  const {
    products,
    categories,
    loading,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
    refreshProducts,
  } = useProducts();
  const { isWithinLimit } = useWorkspace();

  // Product Editor state
  const [editorProduct, setEditorProduct] = useState<Product | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  // Category form state
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const canAddProduct = isWithinLimit('products', products.length);

  const openNewProduct = () => {
    if (!canAddProduct) return;
    setEditorProduct(null);
    setShowEditor(true);
  };

  const openEditProduct = (product: Product) => {
    setEditorProduct(product);
    setShowEditor(true);
  };

  const handleEditorSave = async (product: Product) => {
    try {
      await updateProduct(product.id, product);
      await refreshProducts();
      setShowEditor(false);
    } catch (err) {
      console.error('[Catalog] Failed to save product:', err);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('Delete this product?')) return;
    try {
      await deleteProduct(id);
    } catch (err) {
      console.error('[Catalog] Failed to delete product:', err);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryName.trim());
      } else {
        await addCategory(categoryName.trim());
      }
      setCategoryName('');
      setEditingCategory(null);
      setShowCategoryForm(false);
    } catch (err) {
      console.error('[Catalog] Failed to save category:', err);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Delete this category?')) return;
    try {
      await deleteCategory(id);
    } catch (err) {
      console.error('[Catalog] Failed to delete category:', err);
    }
  };

  const productColumns: Column<Product>[] = [
    {
      key: 'image',
      header: '',
      width: '60px',
      sortable: false,
      render: (p) => (
        <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <img src={p.image || 'https://placehold.co/40x40?text=?'} alt="" className="w-full h-full object-contain" />
        </div>
      ),
    },
    { key: 'name', header: 'Name' },
    {
      key: 'category',
      header: 'Category',
      render: (p) => categories.find(c => c.id === p.category)?.name || '',
    },
    {
      key: 'price',
      header: 'Price',
      render: (p) => `$${p.price.toFixed(2)}`,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Products */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Products</h3>
          <button
            onClick={openNewProduct}
            disabled={!canAddProduct}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition"
          >
            <Plus size={16} />
            Add Product
          </button>
        </div>

        {!canAddProduct && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
            Product limit reached for your plan. Upgrade to add more products.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : (
          <DataTable
            data={products}
            columns={productColumns}
            searchable
            searchPlaceholder="Search products..."
            emptyMessage="No products yet. Click 'Add Product' to create one."
            onRowClick={openEditProduct}
            actions={(p) => (
              <div className="flex gap-1">
                <button
                  onClick={() => openEditProduct(p)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 transition"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDeleteProduct(p.id)}
                  className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-red-500 transition"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          />
        )}
      </div>

      {/* Categories */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Categories</h3>
          <button
            onClick={() => {
              setEditingCategory(null);
              setCategoryName('');
              setShowCategoryForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition"
          >
            <FolderPlus size={16} />
            Add Category
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {categories.map(cat => (
            <div
              key={cat.id}
              className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{cat.name}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setEditingCategory(cat);
                    setCategoryName(cat.name);
                    setShowCategoryForm(true);
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 transition"
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={() => handleDeleteCategory(cat.id)}
                  className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-red-400 transition"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
          {categories.length === 0 && (
            <p className="text-sm text-gray-400 col-span-full">No categories yet.</p>
          )}
        </div>
      </div>

      {/* Product Editor Modal */}
      <ProductEditor
        product={editorProduct}
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={handleEditorSave}
      />

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </h3>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Category name"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowCategoryForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={!categoryName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition"
              >
                {editingCategory ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
